// Cluster ZUWEISEN (B2) — Datenquelle des `KontaktSuchfeld`.
//
// Bewusst KEIN Aufruf von `GET /contact?search`: dessen Antwort hydriert die
// schwere Legacy-Liste, und die Firma eines Ansprechpartners fehlt dort in der
// Hälfte der Fälle (nur `relationsSrc` wird geladen). Der eigene Endpoint
// liefert Typ, Status und Firmenbezug in EINEM Roundtrip.

import { apiUrl } from "../utils/api";
import { gleicherFirmenname, ohneRechtsform } from "../utils/firmennameVergleich";
import { firmenSuchbegriff } from "../utils/visitenkarteFirmensuche";

/** Ein Treffer der Zuweisen-Suche — Spiegel von `ZuweisenTrefferDto` (Backend). */
export type KontaktTreffer = {
    contactId: number;
    displayName: string;
    contactType: "Firma" | "Person" | "Sonstige" | "Unknown";
    /**
     * Fachliche Lesart des Typs. `sonstige` ist eine eigene Klasse und NICHT
     * „privat": an Nicht-Personen lehnt der Server ein `kind`/`companyId` mit
     * 400 ab — die Oberfläche darf dort kein privat/geschäftlich anbieten.
     */
    art: "firma" | "ap" | "privat" | "sonstige";
    /** Primär-Status im Langtext („Kunde", „Verantwortung fürs Unternehmen", …). */
    statusName: string | null;
    /** ERSTE verbundene Firma (= `firmen[0]`) — Vorgangs-Wahl (T-8) und C8 hängen daran. */
    companyId: number | null;
    companyName: string | null;
    defaultEmail: string | null;
    contactNbrInt: string | null;
    /**
     * T-26: alle verbundenen Firmen einer Person, je Firma einmal. Optional
     * typisiert, weil ältere Aufrufer/Fixtures sie nicht kennen — die Anzeige
     * fällt dann auf `companyName` zurück.
     */
    firmen?: KontaktTrefferFirma[];
    /** T-26: Hauptanschrift „Straße, PLZ Ort" oder null. */
    anschrift?: string | null;
    /** T-26: Gruppe des Primär-Status für die K/I/S-Pill. */
    statusGruppe?: KontaktStatusGruppe;
};

/** T-26: Kunde / Interessent / Sonstige — Einteilung wie die Kacheln der Kontaktliste. */
export type KontaktStatusGruppe = "kunde" | "interessent" | "sonstige";

/** T-26: eine Firmenverbindung eines Treffers (Spiegel von `ZuweisenFirmaDto`). */
export type KontaktTrefferFirma = {
    id: number;
    name: string;
    /** Beziehungstyp („Geschäftsführer", „Mensch fürs Unternehmen", …). */
    verbindung: string | null;
    /** Freitext-Funktion („Prokurist", „Fuhrparkmanager", …). */
    funktion: string | null;
};

/** Ab wie vielen Zeichen überhaupt gesucht wird (symmetrisch zum Backend-Gate). */
export const KONTAKT_SUCHE_MIN_ZEICHEN = 2;

/**
 * Kontaktsuche fürs Zuweisen. Unter der Min-Länge wird gar nicht erst gefragt;
 * ein Nicht-OK-Status liefert eine leere Liste (die Oberfläche zeigt dann ihren
 * Fehlerzustand). Ein `AbortError` wird ABSICHTLICH weitergeworfen — der
 * Aufrufer erkennt daran, dass eine neuere Eingabe die alte abgelöst hat.
 */
export async function sucheKontakteFuerZuweisung(
    term: string,
    optionen?: {
        typ?: "firma" | "person";
        limit?: number;
        offset?: number;
        /**
         * Eigene Courté-Kontakte ausblenden (PO 21.09.2026) — Kontakte mit einer
         * E-Mail auf einer Postfach-Domäne des Mandanten. Die Termin-Zuweisung
         * setzt das: einen Termin dem Kollegen zuzuweisen, der ohnehin im
         * Verteiler steht, ergibt keinen Sinn und verdeckt in der Trefferliste
         * nur den Kunden, den man sucht.
         */
        ohneInterne?: boolean;
    },
    signal?: AbortSignal,
): Promise<KontaktTreffer[]> {
    const s = (term ?? "").trim();
    if (s.length < KONTAKT_SUCHE_MIN_ZEICHEN) return [];
    const params: Record<string, string | number> = { search: s };
    if (optionen?.typ) params.typ = optionen.typ;
    if (optionen?.limit) params.limit = optionen.limit;
    if (optionen?.offset) params.offset = optionen.offset;
    if (optionen?.ohneInterne) params.ohneInterne = "1";
    const res = await fetch(apiUrl("contact/zuweisen-suche", params), { signal });
    // Ein HTTP-Fehler (z. B. 502 während eines Backend-Neustarts) ist keine
    // leere Trefferliste — sonst steht „Keine Treffer" da, obwohl gar nicht
    // gesucht wurde. Die Aufrufer fangen den Fehler und bieten „erneut" an.
    if (!res.ok) throw new Error(`Kontaktsuche: HTTP ${res.status}`);
    const body = await res.json().catch(() => null);
    return Array.isArray(body) ? (body as KontaktTreffer[]) : [];
}

/** „Hauptstraße 1, 52062 Aachen" — dieselbe Form wie `KontaktTreffer.anschrift`. */
const anschriftAus = (adresse: any): string | null => {
    if (!adresse) return null;
    const strasse = String(adresse.street ?? "").trim();
    const ort = [adresse.postalCode, adresse.city].map((t) => String(t ?? "").trim()).filter(Boolean).join(" ");
    return [strasse, ort].filter(Boolean).join(", ") || null;
};

/**
 * T-333: Gibt es die Firma mit diesem Namen schon? Liefert die Firmen, deren
 * Name derselbe ist — Groß/Klein und Schreibweise der Rechtsform egal (siehe
 * `gleicherFirmenname`). Mehr als ein Treffer heißt: Dubletten im Bestand,
 * dann wählt der Mensch.
 *
 * Gefragt wird die Namenssuche der Kontaktliste (`nurName=1`, nur Firmen) —
 * dieselbe wie bei der Arbeitgeber-Suche der Anlage. Die Zuweisen-Suche taugt
 * hier nicht: Sie durchsucht auch E-Mail, Telefon und Anschrift und sortiert
 * nach Id, eine gleichnamige Firma könnte also hinter dem Seitenende liegen.
 */
export async function sucheFirmaNachNamen(name: string, signal?: AbortSignal): Promise<KontaktTreffer[]> {
    // Ohne Rechtsform: „Muster Aktiengesellschaft" soll „Muster AG" finden.
    const begriff = firmenSuchbegriff(ohneRechtsform(name ?? ""));
    if (begriff.length < KONTAKT_SUCHE_MIN_ZEICHEN) return [];
    const res = await fetch(
        apiUrl("contact", {
            search: begriff,
            limit: "25",
            filters: JSON.stringify({ Typ: "Firma" }),
            nurName: "1",
            // Wie die Arbeitgeber-Suche: keine persönlichen Kontakte anderer
            // Nutzer, schlanke Form ohne Verträge und Beziehungen.
            ohneFremdePersoenliche: "1",
            include: "",
        }),
        { signal },
    );
    if (!res.ok) throw new Error(`Firmensuche: HTTP ${res.status}`);
    const liste = await res.json().catch(() => null);
    if (!Array.isArray(liste)) return [];
    return liste
        .filter((c: any) => String(c?.type?.type ?? "").toLowerCase() === "firma" && Number(c?.id) > 0)
        .map((c: any): KontaktTreffer => ({
            contactId: Number(c.id),
            displayName: String(c.displayName ?? c.lastname ?? c.lastName ?? "").trim(),
            contactType: "Firma",
            art: "firma",
            statusName: c.status?.status ?? null,
            companyId: null,
            companyName: null,
            defaultEmail: null,
            contactNbrInt: c.contactNbrInt ? String(c.contactNbrInt) : null,
            anschrift: anschriftAus(c.defaultAddress),
        }))
        .filter((t) => t.displayName && gleicherFirmenname(name, t.displayName));
}
